import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';
import {NgbActiveModal, NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import { CollectResponseV6 } from 'app/bankid';
import {fieldsValidation} from 'app/global/fieldvalidation';
import {setRecipientData} from 'app/global/get-recipient-data';
import {GlobalService} from 'app/global/global.service';
import moment from 'moment';
import {DeviceDetectorService} from 'ngx-device-detector';
import Personnummer from 'personnummer';
import {RoleDetail} from '../signing-page/signing-page.dto';
import { AbstractSigningPageComponent } from '../signing-page/abstract-signing-page.component';
import { SingingPageService } from '../signing-page/signing-page-service';
import { DeletedAttachment } from 'app/main/pages/events/editor-emitter.dto';
import { Subject, takeUntil } from 'rxjs';
import { EditorEmitterService } from 'app/main/pages/events/editor-emitter.service';
import { BankIdResponse, BankIdService } from 'app/bankid/bankid.service';
import { BankidQrCodeModalComponent } from 'app/main/pages/general-modals/bankid-qr-code-modal/bankid-qr-code-modal.component';
import { SamsungBrowserInstructionsModalComponent } from 'app/main/pages/general-modals/samsung-browser-instructions-modal/samsung-browser-instructions-modal.component';

export interface Response {
  status: boolean;
  response: object;
}

@Component({
  selector: 'app-sign-bank-id-box',
  templateUrl: './sign-bank-id-box.component.html',
  styleUrls: ['./sign-bank-id-box.component.scss']
})
export class SignBankIdBoxComponent extends AbstractSigningPageComponent implements OnInit, OnDestroy {
  @Input() hasModal: boolean;
  @Input() roleDetail: RoleDetail;
  @ViewChild('bankidQrcode') bankidQrcode;
  constructor(
    private modal: NgbModal,
    private deviceService: DeviceDetectorService,
    public activeModal: NgbActiveModal,
    public globalService: GlobalService,
    private _translocoService:TranslocoService,
    protected singingPage: SingingPageService,
    private editorEmitter: EditorEmitterService,
    private bankIdService: BankIdService,
  ) {
    super(singingPage, globalService, modal)
  }
  public qrcode: string = '';
  public orderRef: string = '';
  public third_party_auth_response:  CollectResponseV6 = {} as  CollectResponseV6;
  public typePersnolNo: string;
  public bankidProcess: boolean = false;
  public isMobile: boolean = false;
  private destroyed = new Subject<void>();
  private deletedAttachment:DeletedAttachment[] = [];
  private cancelBankId: boolean = false;
  private isSamsungInstructionShown: boolean = false;
  public isSamsungBrowser: boolean = false;
  ngOnInit(): void {
    this.roleDetail.recipient = setRecipientData(this.roleDetail.personfields);
    this.isMobile = this.deviceService.isMobile();
    this.editorEmitter.deleteAttachment.pipe(takeUntil(this.destroyed)).subscribe({
      next : (attachment:DeletedAttachment) => {
        this.deletedAttachment = this.deletedAttachment.concat(attachment);
      }
    });
    this.loading.pipe(takeUntil(this.destroyed)).subscribe((loading: boolean) => this.bankidProcess = loading);
    this.isSamsungBrowser = this.globalService.isSamsungBrowser();
  }

  /**
   * generate the qr code for 30 seconds
   * @returns 
   */
  async generateQrcode() {
    const { status } = fieldsValidation(this.globalService.fields_data, this.globalService, String(this.roleDetail.id));
    if (!status || this.bankidProcess) {return false}
    this.bankidProcess = true;
    this.cancelBankId = false;
    const bankid: NgbModalRef = this.modal.open(BankidQrCodeModalComponent, { windowClass: 'bankid-qrcode', backdrop: 'static', keyboard: false });
      bankid.componentInstance.role_id = this.roleDetail?.id
      bankid.componentInstance.persnol_no = this.roleDetail?.recipient?.persnol_no
      bankid.componentInstance.name = (this.roleDetail?.roleable?.visible_name ?? this.roleDetail?.roleable?.name)
      bankid.componentInstance.unique_id = this.roleDetail?.recipient_role_detail?.unique_id
      bankid.componentInstance.apiRouteName = 'bank-id';
      bankid.result.then((response: BankIdResponse) => {
        if (response.status) {
          this.third_party_auth_response = response.response as CollectResponseV6;
          this.readyToSignDocument();
        } else {
          this.globalService.toastrError(this._translocoService.translate("Toaster error message for bankid (Signing page - section) - Unable to sign with BankID. Please try again"));
        }
        this.bankidProcess = false;
      });
  }

  /**
   * sign with the same device Mobile|Window
   */
  async openBankIdDeepLink() {
    if (this.isSamsungBrowser && !this.isSamsungInstructionShown) {
      this.openSamsungHelpModal();
      this.isSamsungInstructionShown = true;
      return;
    }
    const { status } = fieldsValidation(this.globalService.fields_data, this.globalService, String(this.roleDetail.id));
    if (!status || this.bankidProcess) {return false}
    if(!this.roleDetail.recipient.persnol_no && !Personnummer.valid(this.typePersnolNo)){
      this.globalService.toastrWarning(this._translocoService.translate("Toaster warning message for bankid (Signing page - section) - Person number is not valid"));
      return false;
    }
    this.bankidProcess = true;
    const { orderRef, autoStartToken } = await this.bankIdService.signBankid(this.roleDetail?.id, this.roleDetail?.recipient?.persnol_no, (this.roleDetail?.roleable?.visible_name ?? this.roleDetail?.roleable?.name), this.roleDetail?.recipient_role_detail?.unique_id, 'bank-id');
    this.orderRef = orderRef;
    await this.globalService.openBankIdApp(autoStartToken, this.isMobile, this.deviceService.getDeviceInfo().os);
    this.bankIdService.signWithBankId(this.orderRef).then((response) => {
      this.bankidProcess = false;
      this.third_party_auth_response = response.response as CollectResponseV6;
      this.readyToSignDocument();
    }).catch(() => this.bankidProcess = false);
  }

  /**
   * set the user visible data in bankid
   * @returns string
   */
  user_visible_data = () => ( this._translocoService.translate("Bankid user visiable data message (Signing page - section) - I sign document with id on date", {document : this.roleDetail?.roleable?.visible_name ?? this.roleDetail?.roleable?.name, unique_id:this.roleDetail.recipient_role_detail.unique_id, date:moment().format('MMMM Do YYYY, h:mm:ss a')}));

  /**
   * open the sign document modal when bankid success
   */
  readyToSignDocument(){
    this.signTheDocument(this.roleDetail.id, '', '', this.third_party_auth_response, this.roleDetail, this.deletedAttachment, this.globalService.fields_data)
  }

   openSamsungHelpModal() {
    this.modal.open(SamsungBrowserInstructionsModalComponent);
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

}
